import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Award, 
  Users, 
  Clock, 
  Target, 
  Heart, 
  Zap,
  ArrowRight,
  Star,
  Printer
} from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Award className="w-8 h-8" />,
      title: "Excellence",
      description: "Nous nous engageons à livrer la plus haute qualité d'impression pour chaque projet, petit ou grand."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Ponctualité",
      description: "Respect des délais convenus et livraison dans les temps, car votre réussite dépend de la nôtre."
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Service client",
      description: "Écoute, conseils personnalisés et accompagnement à chaque étape de votre projet."
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Innovation",
      description: "Investissement constant dans les dernières technologies d'impression pour des résultats exceptionnels."
    }
  ];

  const milestones = [
    {
      year: "2008",
      title: "Création de DigiPrint",
      description: "Ouverture de notre première imprimerie numérique à Abidjan avec une vision claire : démocratiser l'impression de qualité."
    },
    {
      year: "2012",
      title: "Expansion des services",
      description: "Ajout de l'impression offset et des services de finition pour répondre aux besoins croissants de nos clients."
    },
    {
      year: "2016",
      title: "Modernisation",
      description: "Acquisition d'équipements de dernière génération et formation de notre équipe aux nouvelles technologies."
    },
    {
      year: "2020",
      title: "Service digital",
      description: "Lancement de notre plateforme de commande en ligne et service WhatsApp pour plus de proximité avec nos clients."
    },
    {
      year: "2024",
      title: "Leader du marché",
      description: "Plus de 2000 clients satisfaits et reconnaissance comme référence en impression numérique à Abidjan."
    }
  ];





  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="hero-modern text-neutral-900 py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-display font-bold mb-6 leading-tight text-neutral-900">
                Plus de <span className="text-primary-pink">15 ans</span> d'excellence en impression à Abidjan
              </h1>
              <p className="text-body-large mb-8 text-neutral-600">
                Depuis 2008, DigiPrint Numérique accompagne les entreprises et particuliers 
                ivoiriens dans leurs projets d'impression avec passion et professionnalisme.
              </p>
              <Link
                to="/contact"
                className="btn-secondary inline-flex items-center space-x-2"
              >
                <span>Découvrez nos services</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="text-center">
              <img 
                src="/assets/about-team.jpg" 
                alt="Équipe DigiPrint Abidjan - Service client professionnel"
                className="rounded-2xl shadow-2xl floating-animation mx-auto"
              />
            </div>
          </div>
        </div>
      </section>
      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Notre Histoire
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              En 2008, avec la vision de démocratiser l'accès à une impression de qualité professionnelle 
              à Abidjan, nous avons créé DigiPrint Numérique. Ce qui a commencé comme une petite imprimerie 
              numérique s'est transformé en une entreprise de référence, grâce à notre engagement envers 
              l'excellence et la satisfaction client.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Aujourd'hui, nous sommes fiers d'accompagner plus de 2000 clients - entreprises, institutions, 
              associations et particuliers - dans la réalisation de leurs projets d'impression. Notre succès 
              repose sur trois piliers : la qualité de nos services, la modernité de nos équipements et 
              l'expertise de notre équipe.
            </p>
          </div>
        </div>
      </section>
      {/* Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Notre Parcours
            </h2>
            <p className="text-xl text-gray-600">
              15 années de croissance et d'innovation continue
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {milestones.map((milestone, index) => (
              <div key={index} className="flex flex-col md:flex-row items-center mb-12 last:mb-0">
                <div className="md:w-1/4 mb-4 md:mb-0">
                  <div className="bg-primary-pink text-white text-2xl font-bold py-3 px-6 rounded-lg text-center">
                    {milestone.year}
                  </div>
                </div>
                <div className="md:w-3/4 md:pl-8">
                  <div className="bg-white p-6 rounded-xl shadow-lg">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {milestone.title}
                    </h3>
                    <p className="text-gray-600">
                      {milestone.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos Valeurs
            </h2>
            <p className="text-xl text-gray-600">
              Les principes qui guident notre action au quotidien
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center service-card-hover bg-gray-50 p-8 rounded-xl">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-blue-600">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Mission & Vision */}
      <section className="py-20 hero-accent text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="text-center md:text-left">
              <div className="bg-white bg-opacity-10 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto md:mx-0">
                <Target className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Notre Mission</h3>
              <p className="text-blue-100 leading-relaxed">
                Accompagner nos clients dans la réussite de leurs projets en leur offrant des 
                services d'impression de qualité supérieure, avec des délais rapides et un 
                service client exceptionnel. Nous nous engageons à être le partenaire de 
                confiance de toutes les entreprises et particuliers d'Abidjan.
              </p>
            </div>
            <div className="text-center md:text-left">
              <div className="bg-white bg-opacity-10 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto md:mx-0">
                <Star className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Notre Vision</h3>
              <p className="text-blue-100 leading-relaxed">
                Devenir la référence incontournable en matière d'impression numérique et offset 
                en Côte d'Ivoire. Nous aspirons à être reconnus pour notre innovation constante, 
                notre excellence opérationnelle et notre contribution au développement du secteur 
                de l'impression en Afrique de l'Ouest.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <div className="bg-white rounded-2xl shadow-xl p-12 max-w-4xl mx-auto">
            <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Printer className="w-10 h-10 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Prêt à travailler avec nous ?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Rejoignez plus de 2000 clients satisfaits et découvrez pourquoi DigiPrint 
              est le choix de confiance pour l'impression à Abidjan.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/contact"
                className="btn-primary"
              >
                Demander un devis
              </Link>
              <a
                href="https://wa.me/2250787635341"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg font-medium transition-colors"
              >
                Nous contacter
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;